import React, { useCallback, useEffect, useMemo, useState } from "react";
import { panelistAPI } from "../../services/api";
import CadreDetailsModal from "../shared/CadreDetailsModal";
import { Loading } from "../shared/Loading";
import PanelistCard from "../shared/PanelistCard";

const SelectPanelists = ({ panel, onPanelistsUpdate, currentUser }) => {
  const [availablePanelists, setAvailablePanelists] = useState([]);
  const [selectedPanelists, setSelectedPanelists] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAvailable, setLoadingAvailable] = useState(false);
  const [loadingSelected, setLoadingSelected] = useState(false);
  const [error, setError] = useState(null);

  const fetchAvailablePanelists = useCallback(async () => {
    try {
      setLoadingAvailable(true);
      setError(null);

      const response = await panelistAPI.getAvailablePanelists(panel.id);
      if (response.success) {
        setAvailablePanelists(response.panelists || []);
      }
    } catch (error) {
      console.error("Error fetching available panelists:", error);
      setError("Failed to load available panelists");
    } finally {
      setLoadingAvailable(false);
    }
  }, [panel.id]);

  const fetchSelectedPanelists = useCallback(async () => {
    try {
      setLoadingSelected(true);
      const response = await panelistAPI.getSelectedPanelists(panel.id);
      if (response.success) {
        setSelectedPanelists(response.panelists || []);
      }
    } catch (error) {
      console.error("Error fetching selected panelists:", error);
    } finally {
      setLoadingSelected(false);
    }
  }, [panel.id]);

  useEffect(() => {
    fetchAvailablePanelists();
    fetchSelectedPanelists();
  }, [panel?.id, fetchAvailablePanelists, fetchSelectedPanelists]);

  const filteredPanelists = useMemo(() => {
    return availablePanelists;
  }, [availablePanelists]);

  const selectionStart = useMemo(() => {
    if (!panel?.repSelectionDate) return null;
    return new Date(panel.repSelectionDate);
  }, [panel?.repSelectionDate]);

  const isSelectionOpen = useMemo(() => {
    if (!selectionStart) return false;
    const now = new Date();
    return now >= selectionStart;
  }, [selectionStart]);

  const getPanelistRequirements = () => {
    if (!panel) return { client: 0, community: 0 };

    switch (panel.panelType) {
      case "Community":
        return { client: 2, community: 3 };
      case "Joint Community (2 Communities)":
        return { client: 1, community: 4 };
      case "Joint Community (3 Communities)":
        return { client: 1, community: 3 };
      case "FEMA":
        return { client: 999, community: 0 };
      default:
        return { client: 0, community: 0 };
    }
  };

  const getCurrentSelectionCount = () => {
    // Count based on the panelist's assigned role (determined by who added them)
    const communitySelected = selectedPanelists.filter(
      (panelist) =>
        panelist.addedByRole === "COMMUNITY_MEMBER" ||
        panelist.addedByRole === "CADRE_MEMBER"
    ).length;

    const clientSelected = selectedPanelists.filter(
      (panelist) => panelist.addedByRole === "CLIENT_MEMBER"
    ).length;

    return { community: communitySelected, client: clientSelected };
  };

  const canSelectMore = (member) => {
    if (!currentUser) return false;

    // Only CLIENT_MEMBER and COMMUNITY_MEMBER can select panelists
    if (
      currentUser.role !== "CLIENT_MEMBER" &&
      currentUser.role !== "COMMUNITY_MEMBER"
    ) {
      return false;
    }

    const requirements = getPanelistRequirements();
    const current = getCurrentSelectionCount();

    // Check limits based on current user's role (who is making the selection)
    if (currentUser.role === "COMMUNITY_MEMBER") {
      return current.community < requirements.community;
    } else if (currentUser.role === "CLIENT_MEMBER") {
      if (panel.panelType === "FEMA") {
        return true; // Unlimited for FEMA
      }
      return current.client < requirements.client;
    }
    return false;
  };

  const canEdit = () => {
    if (!currentUser) return false;
    const isRep =
      currentUser.role === "CLIENT_MEMBER" ||
      currentUser.role === "COMMUNITY_MEMBER";
    // Gate by repSelectionDate: selection opens on/after the date
    return isRep && isSelectionOpen;
  };

  const handleMemberSelect = (member) => {
    console.log("handleMemberSelect called with:", member); // Debug log
    
    if (
      !currentUser ||
      (currentUser.role !== "CLIENT_MEMBER" &&
        currentUser.role !== "COMMUNITY_MEMBER")
    ) {
      setError(
        "You do not have permission to select panelists. Only FEMA and Community members can select panelists."
      );
      return;
    }

    if (!isSelectionOpen) {
      const dateText = selectionStart
        ? selectionStart.toLocaleString()
        : "the scheduled selection date";
      setError(`You can perform panelist selection starting on ${dateText}.`);
      return;
    }

    // Always show the modal when clicked - remove the canSelectMore check here
    // The button itself is already disabled if canSelectMore is false
    if (member) {
      console.log("Opening modal with member:", member); // Debug log
      setSelectedMember(member);
      setShowModal(true);
    }
  };

  const handleConfirmSelection = async () => {
    if (!currentUser) {
      setError("User not authenticated");
      return;
    }

    // Check if can select more at confirmation time
    if (!canSelectMore(selectedMember)) {
      setError("Selection limit reached for your role");
      return;
    }

    try {
      setLoading(true);

      const response = await panelistAPI.addPanelist(panel.id, {
        userId: selectedMember.id,
        currentUserId: currentUser.id,
      });

      if (response.success) {
        const newPanelist = {
          id: response.panelist.id,
          panelId: response.panelist.panelId,
          userId: response.panelist.userId,
          role: response.panelist.role,
          status: response.panelist.status,
          joinedDate: response.panelist.joinedDate,
          leftDate: response.panelist.leftDate,
          contribution: response.panelist.contribution,
          rating: response.panelist.rating,
          feedback: response.panelist.feedback,
          createdAt: response.panelist.createdAt,
          updatedAt: response.panelist.updatedAt,
          addedBy: response.panelist.addedBy,
          addedByRole: response.panelist.addedByRole,
          user: response.panelist.user,
          addedByUser: response.panelist.addedByUser,
          name: selectedMember.name,
          email: selectedMember.email,
          phone: selectedMember.phone,
          cadreProfile: selectedMember.cadreProfile,
          conflictOfInterest:
            selectedMember.conflictOfInterestDetail?.evaluatedStatus ||
            selectedMember.panelConflictOfInterest?.evaluatedStatus ||
            selectedMember.conflictOfInterest ||
            "NOT_ANSWERED",
        };

        const updatedSelectedPanelists = [...selectedPanelists, newPanelist];
        setSelectedPanelists(updatedSelectedPanelists);

        setAvailablePanelists((prev) =>
          prev.filter((panelist) => panelist.id !== selectedMember.id)
        );

        setSelectedMember(null);
        setShowModal(false);

        if (onPanelistsUpdate) {
          onPanelistsUpdate(updatedSelectedPanelists);
        }
      } else {
        setError(response.message || "Failed to add panelist");
      }
    } catch (error) {
      console.error("Error adding panelist:", error);
      setError(error.response?.data?.message || "Failed to add panelist");
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePanelist = async (panelistId) => {
    if (!currentUser) {
      setError("User not authenticated");
      return;
    }

    const isRep =
      currentUser.role === "CLIENT_MEMBER" ||
      currentUser.role === "COMMUNITY_MEMBER";
    if (!isRep) {
      setError(
        "You do not have permission to remove panelists. Only FEMA and Community members can remove panelists."
      );
      return;
    }

    if (!isSelectionOpen) {
      const dateText = selectionStart
        ? selectionStart.toLocaleString()
        : "the scheduled selection date";
      setError(`You can perform panelist selection starting on ${dateText}.`);
      return;
    }

    try {
      setLoading(true);

      const response = await panelistAPI.removePanelist(panel.id, panelistId, {
        currentUserId: currentUser.id,
      });

      if (response.success) {
        const removedPanelist = selectedPanelists.find(
          (p) => p.id === panelistId
        );

        const updatedSelectedPanelists = selectedPanelists.filter(
          (p) => p.id !== panelistId
        );
        setSelectedPanelists(updatedSelectedPanelists);

        if (removedPanelist) {
          const originalMember = {
            ...removedPanelist,
            id: removedPanelist.userId || removedPanelist.user?.id,
            name: removedPanelist.user?.name || removedPanelist.name,
            email: removedPanelist.user?.email || removedPanelist.email,
            phone: removedPanelist.user?.phone || removedPanelist.phone,
            role: removedPanelist.user?.role || removedPanelist.role,
            cadreProfile: removedPanelist.cadreProfile,
            conflictOfInterest:
              removedPanelist.conflictOfInterestDetail?.evaluatedStatus ||
              removedPanelist.panelConflictOfInterest?.evaluatedStatus ||
              removedPanelist.conflictOfInterest ||
              "NOT_ANSWERED",
          };

          setAvailablePanelists((prev) => {
            const exists = prev.some((p) => p.id === originalMember.id);
            if (!exists) {
              return [...prev, originalMember];
            }
            return prev;
          });
        } else {
          fetchAvailablePanelists();
        }

        if (onPanelistsUpdate) {
          onPanelistsUpdate(updatedSelectedPanelists);
        }
      } else {
        setError(response.message || "Failed to remove panelist");
      }
    } catch (error) {
      console.error("Error removing panelist:", error);
      setError(error.response?.data?.message || "Failed to remove panelist");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    console.log("Closing modal"); // Debug log
    setShowModal(false);
    setSelectedMember(null);
  };

  const requirements = getPanelistRequirements();
  const current = getCurrentSelectionCount();
  const isRep =
    !!currentUser &&
    (currentUser.role === "CLIENT_MEMBER" ||
      currentUser.role === "COMMUNITY_MEMBER");

  // Don't render if currentUser is not available
  if (!currentUser) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded">
          Loading user information...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {error}
          <button
            onClick={() => setError(null)}
            className="ml-2 text-red-600 hover:text-red-800"
          >
            ×
          </button>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <svg
            className="w-5 h-5 text-blue-600 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
          <div>
            <h3 className="font-semibold text-blue-900">
              {panel?.panelType} Panel
            </h3>
            <p className="text-sm text-blue-800">
              Community: {current.community}/{requirements.community} | FEMA:{" "}
              {current.client}/
              {requirements.client === 999 ? "∞" : requirements.client}
            </p>
            {!canEdit() && (
              <p className="text-sm text-orange-600 mt-1 font-medium">
                View-only mode: You can view panelists but cannot add or remove
                them
              </p>
            )}
          </div>
        </div>
      </div>

      {isRep && !isSelectionOpen && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded">
          {`You can select panelist starting from ${
            selectionStart
              ? selectionStart.toLocaleString()
              : "the scheduled selection date"
          }. Please wait until this date to make your selections.`}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Available Panelists
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Only cadres with NO conflict of interest are shown
          </p>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {loadingAvailable ? (
              <div className="flex justify-center py-8">
                <Loading text="Loading available panelists..." size="md" />
              </div>
            ) : filteredPanelists.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No available panelists found
              </p>
            ) : (
              filteredPanelists.map((panelist) => (
                <div
                  key={panelist.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h4 className="font-medium text-gray-900">
                        {panelist.name || "Unknown Member"}
                      </h4>
                    </div>
                    <p className="text-sm text-gray-600">
                      {panelist.email || "No email"}
                    </p>
                    {panelist.cadreProfile && (
                      <p className="text-xs text-gray-500">
                        {panelist.cadreProfile.yearsExperience || 0} years
                        experience
                      </p>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        console.log("Button clicked for panelist:", panelist); // Debug log
                        handleMemberSelect(panelist);
                      }}
                      disabled={
                        loading || (!canEdit() && !canSelectMore(panelist))
                      }
                      className={`px-3 py-1 text-sm rounded transition-colors ${
                        canEdit()
                          ? "bg-custom-blue text-white hover:bg-custom-light-blue disabled:opacity-50 disabled:cursor-not-allowed"
                          : "bg-gray-300 text-gray-500 cursor-not-allowed"
                      }`}
                    >
                      {canEdit() ? "View & Select" : "View Only"}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Current Panelists ({selectedPanelists.length})
          </h3>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {loadingSelected ? (
              <div className="flex justify-center py-8">
                <Loading text="Loading selected panelists..." size="md" />
              </div>
            ) : selectedPanelists.length === 0 ? (
              <p className="text-gray-500 text-center py-4">
                No panelists selected yet
              </p>
            ) : (
              <>
                {selectedPanelists
                  .filter(
                    (p) =>
                      p.addedByRole === "COMMUNITY_MEMBER" ||
                      p.addedByRole === "CADRE_MEMBER"
                  )
                  .map((panelist) => (
                    <PanelistCard
                      key={panelist.id}
                      panelist={panelist}
                      roleType="COMMUNITY"
                      onRemove={handleRemovePanelist}
                      loading={loading}
                      canRemove={
                        currentUser && panelist.addedBy === currentUser.id
                      }
                      addedByRole={panelist.addedByRole}
                      canEdit={canEdit()}
                    />
                  ))}

                {selectedPanelists
                  .filter((p) => p.addedByRole === "CLIENT_MEMBER")
                  .map((panelist) => (
                    <PanelistCard
                      key={panelist.id}
                      panelist={panelist}
                      roleType="FEMA"
                      onRemove={handleRemovePanelist}
                      loading={loading}
                      canRemove={
                        currentUser && panelist.addedBy === currentUser.id
                      }
                      addedByRole={panelist.addedByRole}
                      canEdit={canEdit()}
                    />
                  ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Modal Component - Added type and data props */}
      <CadreDetailsModal
        isOpen={showModal}
        onClose={handleCloseModal}
        type="cadre"
        data={selectedMember}
        onSelect={handleConfirmSelection}
        showSelectButton={canEdit() && canSelectMore(selectedMember)}
        loading={loading}
      />
    </div>
  );
};

export default SelectPanelists;